import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import regression from 'regression';
import type { ApexOptions } from 'apexcharts';

/**
 * Self-contained port of TraineeSession `ApexChartsMultiZone` (the metrics-tab
 * threshold chart). It keeps the SAME data contract so real data links in by
 * just feeding it the existing shapes:
 *   - `data`: `metrics.map((m, i) => ({ x: i*frequency+1, y: m[key] }))`
 *   - `limits`: the matching `session.metrics_limits` entry
 * The original reads these from Redux; here they are props so the prototype can
 * render with mock data without touching the store.
 */

type DataPoint = { x: number | string; y: number };
type ZoneLimits =
  | {
      lower_limit: number | null;
      upper_limit: number | null;
      enforced: boolean;
    }
  | undefined;

type Props = {
  data: DataPoint[];
  metricName: string;
  limits?: ZoneLimits;
  valueFormatter?: (v: number) => string;
  height?: number | string;
  isDateTimeX?: boolean;
  frequency?: number;
  showRegression?: boolean;
  /** Hide the in-chart title (when the tile already labels the metric). */
  showTitle?: boolean;
  /** Small-multiple mode: trims axes/fonts so it reads at tile size. */
  compact?: boolean;
};

type Zone = { value?: number | null; color: string };
type XY = { x: number; y: number };
type Seg = { name: string; data: XY[]; _color?: string };

const BLUE = '#008efa';
const RED = '#e53935';
const REGRESSION_COLOR = 'rgb(67,67,67)';
const ONE_DAY = 86_400_000;

const formatAxisNumber = (val: number, maxFrac = 2) => {
  if (!Number.isFinite(val)) return '';
  const v = Math.abs(val) < 1e-12 ? 0 : val;
  if (Math.abs(v - Math.round(v)) < 1e-10) return String(Math.round(v));
  return v.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFrac,
  });
};

// Datetime tooltip label for real-time sessions (isDateTimeX). Pinned to UTC so
// the stored market timestamp isn't shifted into a neighbouring day by the
// viewer's timezone — metrics are stamped at 23:30 UTC (EOD) and player_history
// hourly, so the time-of-day is shown too (e.g. "4 May 2018, 23:30").
const formatDateTime = (ms: number): string => {
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

const computeZones = (s: ZoneLimits): Zone[] => {
  if (!s || s.enforced === false) return [{ color: BLUE }];
  const hasOnlyUpper = s.upper_limit !== null && s.lower_limit === null;
  const hasOnlyLower = s.upper_limit === null && s.lower_limit !== null;
  if (hasOnlyUpper)
    return [{ value: s.upper_limit, color: BLUE }, { color: RED }];
  if (hasOnlyLower)
    return [{ value: s.lower_limit, color: RED }, { color: BLUE }];
  return [
    { value: s.lower_limit, color: RED },
    { value: s.upper_limit, color: BLUE },
    { color: RED },
  ];
};

const zoneColorForY = (y: number, zones: Zone[]) => {
  for (const z of zones) {
    if (z.value === null || z.value === undefined) continue;
    if (y <= z.value) return z.color;
  }
  return zones[zones.length - 1]?.color ?? BLUE;
};

const buildSegmentSeries = (points: XY[], zones: Zone[]): Seg[] => {
  if (!points.length) return [];
  const segments: Array<{ color: string; data: XY[] }> = [];
  let currColor = zoneColorForY(points[0].y, zones);
  let currSeg: XY[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const c = zoneColorForY(points[i].y, zones);
    if (c === currColor) {
      currSeg.push(points[i]);
      continue;
    }
    segments.push({ color: currColor, data: currSeg });
    currColor = c;
    currSeg = [points[i - 1], points[i]];
  }
  segments.push({ color: currColor, data: currSeg });
  return segments.map((s, idx) => ({
    name: `Series (${idx + 1})`,
    data: s.data,
    _color: s.color,
  }));
};

const MetricEvolutionChart: React.FC<Props> = ({
  data,
  metricName,
  limits,
  valueFormatter,
  height = 200,
  isDateTimeX = false,
  frequency = 1,
  showRegression = true,
  showTitle = true,
  compact = false,
}) => {
  const processedPoints = useMemo((): XY[] => {
    if (!data?.length) return [];
    return data
      .map(p => ({
        x: typeof p.x === 'string' ? Date.parse(p.x) : p.x,
        y: p.y,
      }))
      .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y));
  }, [data]);

  const regressionSeries = useMemo((): XY[] => {
    if (processedPoints.length < 2) return [];
    if (isDateTimeX) {
      const min = Math.min(...processedPoints.map(p => p.x));
      const reg = regression.linear(
        processedPoints.map(p => [(p.x - min) / ONE_DAY, p.y]),
      );
      return reg.points.map(([nx, y]) => ({ x: nx * ONE_DAY + min, y }));
    }
    const reg = regression.linear(
      // regress on the points' ACTUAL x (== i*frequency+1 on fixed callers; real Day N /
      // datetime-free numeric on predetermined knits)
      processedPoints.map(p => [p.x, p.y]),
    );
    return reg.points.map(([x, y]) => ({ x, y }));
  }, [processedPoints, isDateTimeX]);

  const zones = useMemo(() => computeZones(limits), [limits]);
  const series = useMemo((): Seg[] => {
    const pts = [...processedPoints].sort((a, b) => a.x - b.x);
    const s = buildSegmentSeries(pts, zones);
    if (showRegression && regressionSeries.length) {
      s.push({
        name: 'Trend',
        data: regressionSeries,
        _color: REGRESSION_COLOR,
      });
    }
    return s;
  }, [processedPoints, zones, showRegression, regressionSeries]);

  const colors = useMemo(() => series.map(s => s._color ?? BLUE), [series]);

  const options = useMemo((): ApexOptions => {
    const hasReg = showRegression && regressionSeries.length > 0;
    const fmt = valueFormatter ?? ((v: number) => formatAxisNumber(v, 2));
    // Hug the data range so the line fills the card vertically instead of
    // being squashed by ApexCharts' auto "nice" max (e.g. 30% when data ≈ 15%).
    const ys = [...processedPoints, ...regressionSeries].map(p => p.y);
    const dataMin = ys.length ? Math.min(...ys) : 0;
    const dataMax = ys.length ? Math.max(...ys) : 1;
    const span = dataMax - dataMin || Math.abs(dataMax) || 1;
    const yMin = dataMin - span * 0.12;
    const yMax = dataMax + span * 0.12;
    return {
      chart: {
        type: 'line',
        height,
        parentHeightOffset: 0,
        animations: { enabled: false },
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      // Keep `title` an object even when hidden — setting it to `undefined`
      // clobbers ApexCharts' default and crashes on `title.offsetY`.
      title: {
        text: showTitle ? metricName : undefined,
        style: { fontSize: '12px', fontWeight: 600, color: '#5c6b7f' },
      },
      legend: { show: false },
      colors,
      stroke: {
        curve: 'smooth',
        width: series.map((_, i) =>
          hasReg && i === series.length - 1 ? 1 : compact ? 2 : 3,
        ),
        dashArray: series.map((_, i) =>
          hasReg && i === series.length - 1 ? 5 : 0,
        ),
      },
      markers: { size: 0 },
      xaxis: isDateTimeX
        ? {
            type: 'datetime',
            labels: { show: !compact, style: { fontSize: '11px' } },
          }
        : {
            type: 'numeric',
            tickAmount: compact
              ? 4
              : Math.min(7, Math.max(1, processedPoints.length - 1)),
            axisTicks: { show: !compact },
            axisBorder: { show: !compact },
            labels: {
              show: !compact,
              formatter: (v: string) => formatAxisNumber(Number(v), 0),
              style: { fontSize: '11px' },
            },
          },
      yaxis: {
        min: compact ? yMin : undefined,
        max: compact ? yMax : undefined,
        forceNiceScale: compact ? false : undefined,
        tickAmount: compact ? 2 : undefined,
        labels: {
          offsetX: compact ? -2 : 0,
          formatter: (v: number) => fmt(v),
          style: { fontSize: compact ? '9px' : '11px' },
        },
      },
      tooltip: {
        shared: false,
        intersect: false,
        x: {
          formatter: (v: number) =>
            isDateTimeX ? formatDateTime(v) : `Day ${v}`,
        },
        y: {
          formatter: (v: number) => fmt(v),
          title: { formatter: () => `${metricName}: ` },
        },
      },
      // In compact tiles, negative padding reclaims the space ApexCharts
      // silently reserves so the chart fills the card edge-to-edge while
      // keeping every y-axis label inside the card:
      //  • top -20  → cancels the fixed title/subtitle reservation
      //    (gridShrinkOffset = 10 base + 5 title + 5 subtitle) that exists even
      //    when the title is hidden — grid.padding.top is the only lever for it.
      //    This lifts the plot up flush under the (external) tile title.
      //  • left -10 → pulls the y-axis (labels included) flush to the left edge.
      //    The label's left edge sits at `12 + grid.left + yaxis.offsetX` px, so
      //    -10 with offsetX -2 lands it at 0 (flush, no clipping) and widens the
      //    plot to start right after the labels.
      //  • bottom 0 → the hidden x-axis strip is already thin; pulling it
      //    negative (as top does) drops the lowest y-label below the card, so
      //    keep it at 0 to seat that label flush inside the bottom edge.
      grid: {
        padding: compact
          ? { left: -10, right: 5, top: -20, bottom: 0 }
          : { left: 6, right: 8, top: 0, bottom: 0 },
      },
    };
  }, [
    height,
    metricName,
    colors,
    series,
    isDateTimeX,
    processedPoints,
    showRegression,
    regressionSeries,
    valueFormatter,
    showTitle,
    compact,
  ]);

  const seriesForChart = useMemo(
    () => series.map(({ _color, ...rest }) => rest),
    [series],
  );

  return (
    <Chart
      options={options}
      series={seriesForChart as any}
      type="line"
      height={height}
      width="100%"
    />
  );
};

export default MetricEvolutionChart;
